import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, from, map, Observable, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { filenameFromContentDisposition } from '../shared/file-download';
import { ReleaseReportPreview, ReleaseReportRequest, ReportArt, ReportDownload } from '../models/release-report.model';

const UNSAFE_FILENAME_CHARS = /[^A-Za-z0-9._-]/g;

@Injectable({ providedIn: 'root' })
export class ReleaseReportService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/projects`;

  getPreview(
    projectId: string,
    releaseId: string,
    art: ReportArt,
    planId: string | null,
  ): Observable<ReleaseReportPreview> {
    let params = new HttpParams().set('art', art);
    if (planId) params = params.set('planId', planId);
    return this.http.get<ReleaseReportPreview>(`${this.reportsUrl(projectId, releaseId)}/preview`, { params });
  }

  /** `fallbackSubject` (Plan-Key oder Release-ID) bildet den Dateinamen nur, wenn der Server keinen lesbaren nennt. */
  generate(
    projectId: string,
    releaseId: string,
    request: ReleaseReportRequest,
    fallbackSubject: string,
  ): Observable<ReportDownload> {
    return this.http
      .post(this.reportsUrl(projectId, releaseId), request, { responseType: 'blob', observe: 'response' })
      .pipe(
        map((response) => ({
          blob: response.body ?? new Blob(),
          filename: filenameFromContentDisposition(
            response.headers.get('Content-Disposition'),
            fallbackFilename(request, fallbackSubject),
          ),
        })),
        catchError((err: unknown) => from(readBlobError(err)).pipe(switchMap((e) => throwError(() => e)))),
      );
  }

  private reportsUrl(projectId: string, releaseId: string): string {
    return `${this.base}/${projectId}/releases/${encodeURIComponent(releaseId)}/reports`;
  }
}

function fallbackFilename(request: ReleaseReportRequest, subject: string): string {
  const today = new Date();
  const date = `${today.getFullYear()}${pad(today.getMonth() + 1)}${pad(today.getDate())}`;
  const name = `${request.art.toLowerCase()}_${subject}_${date}`.replace(UNSAFE_FILENAME_CHARS, '_');
  return `${name}.${request.format.toLowerCase()}`;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

// Mit responseType blob kommt auch der Fehler-Body als Blob; erst gelesen liefert er `error` und `code`.
async function readBlobError(err: unknown): Promise<unknown> {
  if (!(err instanceof HttpErrorResponse) || !(err.error instanceof Blob)) return err;
  let body: unknown;
  try {
    body = JSON.parse(await err.error.text());
  } catch {
    body = null;
  }
  return new HttpErrorResponse({
    error: body,
    headers: err.headers,
    status: err.status,
    statusText: err.statusText,
    url: err.url ?? undefined,
  });
}
